import React, { useRef, useEffect } from 'react';
import { Box, Toolbar, IconButton, Divider } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
} from '@mui/icons-material';

const ModernRichTextEditor = ({ content, onChange, error }) => {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && content !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = content || '';
    }
  }, [content]);

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current.focus();
    handleContentChange();
  };

  const handleContentChange = () => {
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleKeyDown = (e) => {
    // Prevent default behavior for Enter key to maintain proper formatting
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      document.execCommand('insertHTML', false, '<br><br>');
      handleContentChange();
    }
  };

  const toolbarButtons = [
    { command: 'bold', icon: <FormatBold />, title: 'Bold' },
    { command: 'italic', icon: <FormatItalic />, title: 'Italic' },
    { command: 'underline', icon: <FormatUnderlined />, title: 'Underline' },
    { divider: true },
    { command: 'insertUnorderedList', icon: <FormatListBulleted />, title: 'Bullet List' },
    { command: 'insertOrderedList', icon: <FormatListNumbered />, title: 'Numbered List' },
    { divider: true },
    { command: 'justifyLeft', icon: <FormatAlignLeft />, title: 'Align Left' },
    { command: 'justifyCenter', icon: <FormatAlignCenter />, title: 'Align Center' },
    { command: 'justifyRight', icon: <FormatAlignRight />, title: 'Align Right' },
  ];

  return (
    <Box
      sx={{
        border: error ? '2px solid #f44336' : '1px solid #e0e0e0',
        borderRadius: '8px',
        backgroundColor: '#fff',
        transition: 'border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          borderColor: error ? '#f44336' : '#ffae00',
        },
        '&:focus-within': {
          borderColor: error ? '#f44336' : '#ffae00',
          boxShadow: error
            ? '0 0 0 3px rgba(244, 67, 54, 0.1)'
            : '0 0 0 3px rgba(25, 118, 210, 0.1)',
        },
      }}
    >
      {/* Toolbar */}
      <Toolbar
        variant="dense"
        sx={{
          minHeight: '48px',
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #e0e0e0',
          borderRadius: '8px 8px 0 0',
          padding: '4px 8px',
          gap: 1,
        }}
      >
        {toolbarButtons.map((button, index) =>
          button.divider ? (
            <Divider key={index} orientation="vertical" flexItem sx={{ mx: 1 }} />
          ) : (
            <IconButton
              key={button.command}
              size="small"
              onClick={() => execCommand(button.command)}
              title={button.title}
              sx={{
                width: 32,
                height: 32,
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: '#e3f2fd',
                  color: '#ffae00',
                },
              }}
            >
              {button.icon}
            </IconButton>
          )
        )}
      </Toolbar>

      {/* Editor Content */}
      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning={true}
        onInput={handleContentChange}
        onKeyDown={handleKeyDown}
        sx={{
          minHeight: '200px',
          padding: '16px',
          outline: 'none',
          fontSize: '18px',
          lineHeight: 1.6,
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          color: '#333',
          backgroundColor: '#fff',
          borderRadius: '0 0 8px 8px',
          '&:focus': {
            outline: 'none',
          },
          '& p': {
            margin: '0 0 8px 0',
            '&:last-child': {
              marginBottom: 0,
            },
          },
          '& ul, & ol': {
            paddingLeft: '20px',
            margin: '8px 0',
          },
          '& li': {
            margin: '4px 0',
          },
          '& br': {
            lineHeight: 1.6,
          },
          // Ensure text appears properly
          '&:empty:before': {
            content: '"Start typing your message..."',
            color: '#9e9e9e',
            fontStyle: 'italic',
          },
        }}
      />
    </Box>
  );
};

export default ModernRichTextEditor;